import { OrderStatus, RestaurantsOrderStatus } from '@wix/restaurants-types';
import { SortOrder } from '@wix/ambassador-ecom-v1-order/types';
import mergeWith from 'lodash/mergeWith';
import isArray from 'lodash/isArray';
import { CreateDeliveryInvalidTimeInstructionsError, CreateDeliveryInvalidTimeInstructionsType } from '../../errors/request-options';
import { DATA_EXTENSIONS_USER_FIELDS_NAMESPACE, ECOM_ORDERS_FQDN } from '@wix/restaurants-consts';
function withArrayConcatenation(objValue, srcValue) {
  if (isArray(objValue)) {
    return objValue.concat(srcValue);
  }
  return;
}
export const requestOptions = {
  queryOrders(_temp) {
    let {
      queryMerge,
      options = {}
    } = _temp === void 0 ? {} : _temp;
    const {
      includeCanceled,
      filterOverdueOrdersByNHours,
      range,
      includeOrdersWithoutFulfillmentTime = false,
      offset,
      shouldSortOnlyByNumber = false,
      locationId
    } = options;
    const deliveryTimeSlotFromDateStart = range != null && range.from ? {
      $gte: range.from.toISOString()
    } : undefined;
    const deliveryTimeSlotFromDateEnd = range != null && range.to ? {
      $lte: range.to.toISOString()
    } : undefined;
    const overdueDate = filterOverdueOrdersByNHours !== undefined ? (() => {
      const dateTime = new Date();
      dateTime.setHours(dateTime.getHours() - filterOverdueOrdersByNHours);
      return dateTime;
    })() : undefined;
    const deliveryTimeSlotToDate = overdueDate ? {
      $gte: overdueDate.toISOString()
    } : undefined;
    const deliveryTimeSlotQueries = [...(deliveryTimeSlotFromDateStart ? [{
      deliveryTimeSlotFromDate: deliveryTimeSlotFromDateStart
    }] : []), ...(deliveryTimeSlotFromDateEnd ? [{
      deliveryTimeSlotFromDate: deliveryTimeSlotFromDateEnd
    }] : []), ...(deliveryTimeSlotToDate ? [{
      deliveryTimeSlotToDate
    }] : [])];
    const deliveryTimeSlotQueriesMergeWithMissingDeliveryTimeQuery = deliveryTimeSlotQueries.length === 0 ? [] : [{
      $or: [...(deliveryTimeSlotQueries.length > 1 ? [{
        $and: [...deliveryTimeSlotQueries]
      }] : deliveryTimeSlotQueries), {
        $and: [{
          deliveryTimeSlotFromDate: {
            $exists: false
          }
        }, {
          deliveryTimeSlotToDate: {
            $exists: false
          }
        }]
      }]
    }];
    const NUMBER_SORTING = {
      field_name: 'number',
      order: SortOrder.ASC
    };
    return mergeWith({
      query: {
        filter: {
          $and: [{
            'lineItems.catalogReference.appId': {
              $eq: '9a5d83fd-8570-482e-81ab-cfa88942ee60'
            }
          }, {
            archived: {
              $eq: false
            }
          }, {
            status: {
              $in: [OrderStatus.APPROVED, ...(includeCanceled ? [OrderStatus.CANCELED] : [])]
            }
          }, ...(includeOrdersWithoutFulfillmentTime ? deliveryTimeSlotQueriesMergeWithMissingDeliveryTimeQuery : deliveryTimeSlotQueries), ...(locationId ? [{
            'businessLocation.id': {
              $eq: locationId
            }
          }] : [])]
        },
        sort: [...(shouldSortOnlyByNumber ? [NUMBER_SORTING] : [{
          field_name: 'deliveryTimeSlotFromDate',
          order: SortOrder.ASC
        }, {
          field_name: 'deliveryTimeSlotToDate',
          order: SortOrder.ASC
        }, {
          field_name: 'shippingInfo.title',
          order: SortOrder.ASC
        }, NUMBER_SORTING])],
        paging: {
          limit: 100,
          offset
        }
      }
    }, queryMerge, withArrayConcatenation);
  },
  queryOrdersWithoutLocation(_temp2) {
    let {
      options = {}
    } = _temp2 === void 0 ? {} : _temp2;
    const {
      filterOverdueOrdersByNHours,
      includeOrdersWithoutFulfillmentTime,
      offset,
      range,
      operationalStatuses
    } = options;
    return this.queryOrders({
      queryMerge: {
        query: {
          filter: {
            $and: [{
              'businessLocation.id': {
                $exists: false
              }
            }, ...(operationalStatuses ? [{
              fulfillmentStatuses: {
                $in: operationalStatuses
              }
            }] : [])]
          }
        }
      },
      options: {
        filterOverdueOrdersByNHours,
        includeOrdersWithoutFulfillmentTime,
        offset,
        range,
        includeCanceled: false,
        shouldSortOnlyByNumber: true
      }
    });
  },
  queryFulfillmentMethods(_temp3) {
    let {
      queryMerge,
      fulfillmentIds
    } = _temp3 === void 0 ? {} : _temp3;
    return mergeWith({
      query: {
        filter: {
          ...(fulfillmentIds ? {
            id: {
              $in: fulfillmentIds
            }
          } : {})
        }
      }
    }, queryMerge, withArrayConcatenation);
  },
  cancelOrder(orderId, options) {
    return {
      id: orderId,
      ...options
    };
  },
  getOrder(orderId) {
    return {
      id: orderId
    };
  },
  updateOrder(order, fieldMask) {
    return {
      // Type fix - ensure we pass id: any
      order: {
        ...order,
        id: order.id
      },
      fieldMask
    };
  },
  getOrderFulfillmentList(orderId) {
    return {
      orderId
    };
  },
  getMultipleOrderFulfillmentList(orderIds) {
    return {
      orderIds
    };
  },
  updateFulfillmentStatus(orderId, fulfillmentId, toStatus) {
    return {
      orderId,
      fulfillment: {
        id: fulfillmentId,
        status: toStatus,
        completed: toStatus === RestaurantsOrderStatus.FULFILLED
      },
      fieldMask: ['status', 'completed']
    };
  },
  archiveOrder(orderId) {
    return {
      id: orderId
    };
  },
  getSiteProperties(fieldMasks) {
    return fieldMasks ? {
      fields: fieldMasks
    } : {};
  },
  getListOperations() {
    return {};
  },
  getAvailableFulfillmentOptions(operationId, deliveryAddress) {
    return {
      operationId,
      deliveryAddress
    };
  },
  scheduleDelivery(_ref) {
    let {
      wixOrderId,
      providerAppDefId,
      ...timeInstruction
    } = _ref;
    let dropOffTime, pickupTime;
    if ('dropOffTime' in timeInstruction) {
      dropOffTime = timeInstruction.dropOffTime;
    }
    if ('pickupTime' in timeInstruction) {
      pickupTime = timeInstruction.pickupTime;
    }
    if (!dropOffTime && !pickupTime) {
      throw new CreateDeliveryInvalidTimeInstructionsError(CreateDeliveryInvalidTimeInstructionsType.NONE_PROVIDED);
    }
    if (dropOffTime && pickupTime) {
      throw new CreateDeliveryInvalidTimeInstructionsError(CreateDeliveryInvalidTimeInstructionsType.BOTH_PROVIDED);
    }
    return {
      providerAppDefId,
      wixOrderId,
      dropOffTime,
      pickupTime
    };
  },
  /**
   * @deprecated use {@link scheduleDelivery} instead of acceptDeliveryOrderOptions
   */
  acceptDeliveryOrderOptions(_ref2) {
    let {
      orderId,
      ...timeInstruction
    } = _ref2;
    const dropOffTime = timeInstruction.dropOffTime;
    const pickupTime = timeInstruction.pickupTime;
    if (!dropOffTime && !pickupTime) {
      throw new CreateDeliveryInvalidTimeInstructionsError(CreateDeliveryInvalidTimeInstructionsType.NONE_PROVIDED);
    }
    if (dropOffTime && pickupTime) {
      throw new CreateDeliveryInvalidTimeInstructionsError(CreateDeliveryInvalidTimeInstructionsType.BOTH_PROVIDED);
    }
    return {
      orderId,
      dropOffTime,
      pickupTime
    };
  },
  /**
   * @deprecated use {@link scheduleDelivery} instead of createDeliveryWithProvider
   */
  createDeliveryWithProvider(_ref3) {
    let {
      providerAppDefId,
      ...createDeliveryParams
    } = _ref3;
    return {
      ...this.acceptDeliveryOrderOptions(createDeliveryParams),
      providerAppDefId
    };
  },
  getDeliveryProviders(_ref4) {
    let {
      businessLocationId,
      cursorPaging
    } = _ref4;
    if (cursorPaging) {
      return {
        businessLocationId,
        cursorPaging
      };
    }
    return {
      businessLocationId
    };
  },
  /**
   * @deprecated use {@link getDeliveryProviders} instead
   */
  getListDeliveryProviders(businessLocationId) {
    return {
      businessLocationId
    };
  },
  getOrderTransactionsList(orderId) {
    return {
      orderId
    };
  },
  getMultipleOrderTransactionsList(orderIds) {
    return {
      orderIds
    };
  },
  listDataExtensionSchemas() {
    return {
      fqdn: ECOM_ORDERS_FQDN,
      namespaces: [DATA_EXTENSIONS_USER_FIELDS_NAMESPACE]
    };
  }
};
//# sourceMappingURL=request-options.js.map